import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { PageHeader, Badge } from "../../components/ui-extras";
import {
  TICKET_STATUSES, TICKET_CATEGORIES,
  ticketStatusLabel, ticketCategoryLabel, ticketStatusVariant, formatIso,
} from "../../lib/tickets";

export default function AdminSupportTickets() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");
  const [categoryFilter, setCategoryFilter] = useState("");

  const reload = () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (categoryFilter) params.category = categoryFilter;
    api.get("/admin/support-tickets", { params })
      .then(({ data }) => setRows(data || []))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [statusFilter, categoryFilter]);

  return (
    <>
      <PageHeader
        title="Support tickets"
        subtitle="ADMIN / SUPPORT"
        testId="admin-support-header"
      />

      <div className="gf-card mb-4">
        <div className="gf-card-body grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="overline text-[10px]">STATUS</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="gf-input w-full"
              data-testid="admin-support-status-filter"
            >
              <option value="">All</option>
              {TICKET_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="overline text-[10px]">CATEGORY</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="gf-input w-full"
              data-testid="admin-support-category-filter"
            >
              <option value="">All</option>
              {TICKET_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="text-xs text-zinc-500">
            Showing <strong>{rows.length}</strong> tickets.
          </div>
        </div>
      </div>

      <div className="gf-card">
        <table className="gf-table" data-testid="admin-support-table">
          <thead>
            <tr>
              <th>OPENED</th>
              <th>CUSTOMER</th>
              <th>SUBJECT</th>
              <th>CATEGORY</th>
              <th>STATUS</th>
              <th>LAST UPDATE</th>
              <th>BY</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center text-zinc-400 py-10">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="text-center text-zinc-400 py-10"
                  data-testid="admin-support-empty"
                >
                  Nothing matches your filters.
                </td>
              </tr>
            )}
            {rows.map((t) => (
              <tr
                key={t.ticket_id}
                data-testid={`admin-support-row-${t.ticket_id}`}
                className="hover:bg-zinc-50"
              >
                <td className="font-mono text-xs">{formatIso(t.opened_at)}</td>
                <td className="text-sm">{t.customer_name || t.customer_id}</td>
                <td>
                  <Link
                    to={`/support-tickets/${t.ticket_id}`}
                    className="font-medium text-zinc-900 hover:text-orange-600"
                    data-testid={`admin-support-link-${t.ticket_id}`}
                  >
                    {t.subject}
                  </Link>
                  <div className="text-xs text-zinc-400 truncate max-w-md">
                    {t.last_message_preview}
                  </div>
                </td>
                <td>
                  <Badge variant="info">{ticketCategoryLabel(t.category)}</Badge>
                </td>
                <td>
                  <Badge variant={ticketStatusVariant(t.status)}>
                    {ticketStatusLabel(t.status)}
                  </Badge>
                </td>
                <td className="font-mono text-xs">{formatIso(t.last_message_at)}</td>
                <td className="overline text-[10px]">
                  {t.last_message_role || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
